import { createStyles, Group, MantineProvider, Menu } from "@mantine/core";
import { rpaTheme } from "../../theme/rpaTheme";
import { OrdersTableFooter, OrdersTableV2 } from "./OrdersTableV2";
import { GraphQL, isGraphQLError } from "@rpi/openapi-api";
import { PaginationV2 } from "../../components/PaginationV2";
import React from "react";
import { useRpiPagination } from "../../hooks/usePagination";
import { useAuthUser } from "../../hooks/useAuthUser";
import { Button, Input, tokens } from "@blurb/codex-react";
import { useForm } from "@mantine/form";
import { IconV3, useToasts } from "@rpi/openapi-core";
import { AddPaymentMethod } from "../../components/Alerts/AddPaymentMethodAlert";
import { FilterChips } from "../../components/FilterChips";
import { SearchInput } from "../../configuration/filters/searchInput";
import { orderSearchLabels, orderSearchRenderers } from "../../utils/searchField.util";
import { Heading } from "../../components/Typography";

const useStyles = createStyles({
  container: {
    width: "100%",
    height: "100%",
    overflow: "auto",
    padding: tokens.spacing[20],
    display: "flex",
    flexDirection: "column",
    color: tokens.color.semantic.text.bold,
    gap: tokens.spacing[6],
  },
  row: { display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "flex-end" },
  filtersContainer: { display: "flex", flexDirection: "column", gap: tokens.spacing[4] },
  search: { display: "flex", flexDirection: "row", alignItems: "center", gap: tokens.spacing[2] },
  searchInput: {
    backgroundColor: tokens.color.semantic.bg.input,
    width: 400,
  },
  table: {
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 4px 12px 1px rgba(0, 0, 0, 0.10)",
    borderRadius: tokens.radius.lg,
  },
});
export const OrdersViewV2 = () => {
  const user = useAuthUser();
  const pagination = useRpiPagination({ perPage: 20 });
  const { classes } = useStyles();

  // Table Filters
  const [sortInput, setSortInput] = React.useState<GraphQL.SortInput | undefined>({
    by: GraphQL.OrderSearchField.CreatedAt,
    sortDirection: GraphQL.SortDirection.Desc,
  });

  const [generalSearch, setGeneralSearch] = React.useState<string | undefined>();

  const [searchFilters, setSearchFilters] = React.useState<SearchInput<GraphQL.OrderSearchField>[]>([]);

  const sandboxFilter = {
    field: GraphQL.OrderSearchField.IsSandbox,
    condition: GraphQL.SearchCondition.Equals,
    firstValue: user.isSandbox.toString(),
  };

  const form = useForm<{ searchValue: string }>({ initialValues: { searchValue: "" } });

  const handleSearchOrdersSubmit = (input: { searchValue: string }) => {
    setGeneralSearch(input.searchValue || undefined);
    pagination.firstPage();
  };

  const { data, isLoading, error } = GraphQL.useOrdersQuery(
    {
      limit: pagination.perPage,
      offset: pagination.offset,
      search: {
        inputs: [sandboxFilter],
        generalInput: generalSearch, // generalInput searches by customerOrderId
        sort: sortInput,
      },
    },
    {
      onSuccess: (data) => pagination.setTotalCount(data.orders.totalCount),
    }
  );
  const orders = data?.orders.data;

  const { showSuccessToast, showErrorToast } = useToasts();

  const { mutate: sendSandboxPaymentNotification, isLoading: isSandboxPaymentLoading } =
    GraphQL.useSandboxPaymentNotificationMutation({
      onSuccess: (data) => {
        if (data.sandboxPaymentNotification.success) {
          showSuccessToast(
            "Payment success webhook sent! Please check your endpoint and inbox for the confirmation email."
          );
        } else {
          // success denotes whether or not a success event was sent and indicates a successful submission either way
          showSuccessToast("Payment failed webhook sent! Please check your endpoint.");
        }
      },
      onError: (error) => {
        if (isGraphQLError(error)) {
          showErrorToast(
            'Payment webhook test failed. Make sure you have orders in "Awaiting Payment" status and try again.'
          );
        } else {
          showErrorToast("Sandbox payment notification failed.");
        }
      },
    });

  return (
    <MantineProvider theme={rpaTheme}>
      <div className={classes.container} data-testid="orders-view" aria-label="Orders view">
        <Heading size="6xl">Orders</Heading>
        <AddPaymentMethod />
        <div className={classes.row}>
          <div className={classes.filtersContainer}>
            <form className={classes.search} onSubmit={form.onSubmit(handleSearchOrdersSubmit)}>
              <div className={classes.searchInput}>
                <Input
                  iconLeft={<IconV3 icon="search" size={24} />}
                  placeholder="Search by Order ID"
                  disabled={isLoading}
                  {...form.getInputProps("searchValue")}
                />
              </div>
              <Button variant="outlined" intent="neutral" type="submit" disabled={isLoading}>
                Search
              </Button>
            </form>
            {searchFilters.length > 0 && (
              <FilterChips
                filters={searchFilters}
                onSetFilters={setSearchFilters}
                filterLabels={orderSearchLabels}
                valueRenderers={orderSearchRenderers}
              />
            )}
          </div>

          {user.isSandbox && (user.is.CUSTOMER_ADMIN || user.is.USER) && (
            <Menu width={240} position="bottom-end" withinPortal>
              <Menu.Target>
                <Button
                  variant="filled"
                  intent="primary"
                  size="large"
                  loading={isSandboxPaymentLoading}
                  aria-label={"Test Order Payments"}
                >
                  <Group>
                    <IconV3 icon="credit_card" color="inherit" />
                    Test Order Payments
                  </Group>
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item onClick={() => sendSandboxPaymentNotification({ input: { success: true } })}>
                  Payment Success
                </Menu.Item>
                <Menu.Item onClick={() => sendSandboxPaymentNotification({ input: { success: false } })}>
                  Payment Failed
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          )}
        </div>
        <div className={classes.table} data-testid="orders-table" aria-label="Orders table">
          <OrdersTableV2
            isLoading={isLoading}
            error={error}
            orders={orders}
            withSortBy={{
              defaultValue: sortInput,
              onChange: setSortInput,
            }}
          >
            <OrdersTableFooter>
              <PaginationV2 pagination={pagination} isLoading={isLoading} />
            </OrdersTableFooter>
          </OrdersTableV2>
        </div>
      </div>
    </MantineProvider>
  );
};
